import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';

import type { ChangeKind } from '../turn-edits/changeKinds';

import { checkBlankScene } from './checks/blankScene';
import { checkCaptionOverlap } from './checks/captionOverlap';
import { checkDuplicateMedia } from './checks/duplicateMedia';
import { checkDuplicateText } from './checks/duplicateText';
import { checkLateSceneContent } from './checks/lateSceneContent';
import { checkNoScenes } from './checks/noScenes';
import { checkOverlayCoverage } from './checks/overlayCoverage';
import { checkVoicedrivenSceneReadingTime } from './checks/voicedrivenSceneReadingTime';
import { checkVoicedrivenSceneTooLong } from './checks/voicedrivenSceneTooLong';
import { checkPlaceholderIcon } from './checks/placeholderIcon';
import { checkPlaceholderText } from './checks/placeholderText';
import { checkQuoteDesignWithoutAttribution } from './checks/quoteDesignWithoutAttribution';
import { checkMatchCutOpportunities } from './checks/matchCutOpportunities';
import { checkMusicTooShort } from './checks/musicTooShort';
import { checkSceneReadingTime } from './checks/sceneReadingTime';
import { checkTextClipping } from './checks/textClipping';
import { checkTextContrast } from './checks/textContrast';
import { checkTextOffscreen } from './checks/textOffscreen';
import { checkTextWidows } from './checks/textWidows';
import { checkVoiceGroupMissingTranscript } from './checks/voiceGroupMissingTranscript';

import { ErrorReporter } from 'js/src/libs/error-reporter';

export type LintSeverity = 'error' | 'warning';

export type LintCheckName =
    | 'no_scenes'
    | 'voice_group_missing_transcript'
    | 'overlay_coverage'
    | 'caption_overlap'
    | 'text_contrast'
    | 'text_clipping'
    | 'text_offscreen'
    | 'text_widows'
    | 'duplicate_text'
    | 'duplicate_media'
    | 'placeholder_text'
    | 'placeholder_icon'
    | 'quote_design_without_attribution'
    | 'match_cut_opportunities'
    | 'music_too_short'
    | 'scene_reading_time'
    | 'voicedriven_scene_reading_time'
    | 'voicedriven_scene_too_long'
    | 'blank_scene'
    | 'late_scene_content';

export interface LintFinding {
    check: LintCheckName;
    severity: LintSeverity;
    /**
     * The stable id of the scene the finding is about, or of every scene it spans. The lint
     * report shows the short form of this id so the agent can address the scene directly with the
     * same scene_id the edit tools (set_text/set_media/set_icon) take — never a positional index,
     * which drifts as scenes are added (AP-931). Scene-group-level findings use sceneGroupIndex
     * instead, since scene groups are still addressed by index.
     */
    sceneId?: string | string[];
    sceneGroupIndex?: number;
    assetName?: string;
    message: string;
}

export interface LintReport {
    findings: LintFinding[];
    checksRun: LintCheckName[];
}

/**
 * A single lint check. Operates on one Luminary tree and returns the findings
 * it produced. Async because some checks (e.g. text contrast) render frames.
 */
export type LintCheck = (
    luminary: Luminary,
    getState: GetRootState,
) => LintFinding[] | Promise<LintFinding[]>;

/**
 * A check, and the kinds of change that can cause its findings. On an edit to an existing video,
 * lint asks the agent to fix only the findings its change could have caused (see `TurnEdits`): a
 * finding counts when a scene it names was added this turn, or changed in one of these ways. A
 * check that reads a scene's words lists 'text', one that reads its timing lists 'timing', and so on.
 */
type LintCheckDefinition = {
    run: LintCheck;
    causedBy: readonly ChangeKind[];
};

const CHECKS: Record<LintCheckName, LintCheckDefinition> = {
    no_scenes: { run: checkNoScenes, causedBy: ['scene_list'] },
    voice_group_missing_transcript: {
        run: checkVoiceGroupMissingTranscript,
        causedBy: ['group_timing'],
    },
    overlay_coverage: { run: checkOverlayCoverage, causedBy: ['timing', 'group_timing'] },
    caption_overlap: { run: checkCaptionOverlap, causedBy: ['text', 'layout', 'captions'] },
    text_contrast: { run: checkTextContrast, causedBy: ['text', 'layout', 'colour', 'media'] },
    text_clipping: { run: checkTextClipping, causedBy: ['text', 'layout'] },
    text_offscreen: { run: checkTextOffscreen, causedBy: ['text', 'layout'] },
    text_widows: { run: checkTextWidows, causedBy: ['text', 'layout'] },
    duplicate_text: { run: checkDuplicateText, causedBy: ['text'] },
    duplicate_media: { run: checkDuplicateMedia, causedBy: ['media'] },
    placeholder_text: { run: checkPlaceholderText, causedBy: ['text'] },
    placeholder_icon: { run: checkPlaceholderIcon, causedBy: ['media'] },
    quote_design_without_attribution: {
        run: checkQuoteDesignWithoutAttribution,
        causedBy: ['text', 'layout'],
    },
    // An opportunity, not a defect an edit causes: only a scene the turn added brings one in.
    match_cut_opportunities: { run: checkMatchCutOpportunities, causedBy: [] },
    music_too_short: { run: checkMusicTooShort, causedBy: ['music'] },
    scene_reading_time: { run: checkSceneReadingTime, causedBy: ['text', 'timing'] },
    voicedriven_scene_reading_time: {
        run: checkVoicedrivenSceneReadingTime,
        causedBy: ['text', 'timing', 'group_timing'],
    },
    voicedriven_scene_too_long: {
        run: checkVoicedrivenSceneTooLong,
        causedBy: ['timing', 'group_timing'],
    },
    blank_scene: { run: checkBlankScene, causedBy: ['text', 'layout', 'colour', 'media'] },
    late_scene_content: {
        run: checkLateSceneContent,
        causedBy: ['text', 'media', 'timing', 'group_timing'],
    },
};

export const ALL_LINT_CHECKS = Object.keys(CHECKS) as LintCheckName[];

/** The kinds of change that can cause a check's findings. */
export function lintCheckCausedBy(check: LintCheckName): readonly ChangeKind[] {
    return CHECKS[check].causedBy;
}

/**
 * The severities each check can raise. Analytics lists a check only under these, so a warning-only
 * check doesn't report an `error_*` count that is always zero. Keep in step with the checks.
 */
export const LINT_CHECK_SEVERITIES: Record<LintCheckName, readonly LintSeverity[]> = {
    no_scenes: ['error'],
    voice_group_missing_transcript: ['error'],
    overlay_coverage: ['error'],
    caption_overlap: ['error'],
    text_contrast: ['error'],
    text_clipping: ['error'],
    text_offscreen: ['error'],
    text_widows: ['warning'],
    duplicate_text: ['warning'],
    duplicate_media: ['warning'],
    placeholder_text: ['warning'],
    placeholder_icon: ['warning'],
    quote_design_without_attribution: ['warning'],
    match_cut_opportunities: ['warning'],
    music_too_short: ['error', 'warning'],
    scene_reading_time: ['warning'],
    voicedriven_scene_reading_time: ['warning'],
    voicedriven_scene_too_long: ['warning'],
    blank_scene: ['error'],
    late_scene_content: ['error'],
};

/**
 * Runs the configured battery of read-only quality checks against a single
 * Luminary tree and returns one combined report. Each check is isolated: a
 * check that throws is skipped rather than failing the whole run, so one
 * broken check never hides the findings of the others.
 */
export async function runVideoLint(
    luminary: Luminary,
    getState: GetRootState,
    options: { only?: LintCheckName[] } = {},
): Promise<LintReport> {
    const checksToRun = options.only?.length ? options.only : ALL_LINT_CHECKS;
    const findings: LintFinding[] = [];

    for (const checkName of checksToRun) {
        const check = CHECKS[checkName].run;
        if (!check) {
            continue;
        }
        try {
            const checkFindings = await check(luminary, getState);
            findings.push(...checkFindings);
        } catch (error) {
            ErrorReporter.capture(error, {
                level: 'warning',
                extra: { message: `Lint check "${checkName}" failed` },
            });
        }
    }

    return { findings, checksRun: checksToRun };
}
